import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, ShapesIcon, TimerIcon, TrophyIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/cf34376b-886e-4822-ab8a-072af32e6c4d/1792461549223_tk-mockup-963a95c4.webp';
export const heroGuarantees = ['Omedelbar digital nedladdning', 'Engångsbetalning', 'Ingen prenumeration', '7 dagars nöjdhetsgaranti'];
export const stats = [{
  value: '240',
  label: 'Sidor i huvudvolymerna'
}, {
  value: '6',
  label: 'Digitala volymer'
}, {
  value: '4',
  label: 'Bollnivåer – röd, orange, grön, gul'
}, {
  value: '5',
  label: 'Bonusar som ingår'
}, {
  value: '1',
  label: 'Komplett tränarsystem'
}];
export const differenceStats = [{
  value: 'Alla nivåer',
  label: 'Från röd boll till tävlingsspel'
}, {
  value: '6 digitala volymer',
  label: 'Från årsplanering och teknik till taktik och uppföljning'
}, {
  value: 'Utskrivbara PDF-filer',
  label: 'Att använda digitalt eller ta med ut på banan'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Kvällar och helger försvinner',
  text: 'Timmar framför datorn för att planera veckans pass – och du är fortfarande inte klar.'
}, {
  title: 'Timmar av letande på YouTube',
  text: 'Övningar från olika videor och sajter är för lätta, för svåra eller måste anpassas innan de fungerar på banan.'
}, {
  title: 'Grupper med blandade nivåer',
  text: 'Nybörjare och mer vana spelare på samma bana – och ett pass som ska fungera för alla.'
}, {
  title: 'Barn som tappar fokus',
  text: 'När spelarna står i kö och väntar på sin tur sjunker energin och passet tappar tempo.'
}, {
  title: 'Svårt att visa utvecklingen',
  text: 'Föräldrar vill veta hur barnen utvecklas, men tester och uppföljning blir ännu mer arbete efter passen.'
}, {
  title: 'Stress när bantiden börjar',
  text: 'När tiden inte räcker till blir det lätt att improvisera och hitta på övningar i sista minuten.'
}];
export const afterItems = ['Ett organiserat tränarsystem för alla nivåer', 'Färdiga pass minut för minut', 'Teknikövningar slag för slag', 'Passkort som kan skrivas ut direkt', 'Banskisser och tydliga uppställningar', 'Lekar och matchformer för varje bollfärg', 'Uppvärmningar och skadeprevention', 'Nivåtester och föräldrarapporter', 'Säsongsplanering för hela året'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Kvällarna går åt till planering',
  text: 'Lägger du kvällar och helger på att planera passen inför kommande vecka?'
}, {
  icon: FolderSearchIcon,
  title: 'Du vet inte vilken övning du ska köra',
  text: 'Letar du ständigt på YouTube efter övningar som passar gruppens nivå?'
}, {
  icon: UsersIcon,
  title: 'Gruppen har blandade nivåer',
  text: 'Behöver du övningar som fungerar för både nybörjare och mer vana spelare på samma bana?'
}, {
  icon: TimerIcon,
  title: 'Spelarna står i kö för länge',
  text: 'Önskar du att passen hade mer bollkontakt, högre tempo och mindre väntan?'
}, {
  icon: TrophyIcon,
  title: 'Du vill visa utvecklingen',
  text: 'Vill du kunna visa spelare och föräldrar hur de utvecklas från bollfärg till bollfärg?'
}, {
  icon: LayoutGridIcon,
  title: 'Du behöver en tydligare struktur',
  text: 'Vill du ha allt samlat så att du snabbt hittar rätt pass när bantiden börjar?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volym 01',
  level: 'Alla nivåer',
  title: 'Årsplanering & 12 färdiga pass',
  tagline: 'Vet exakt vad varje pass ska innehålla – hela säsongen.',
  intro: 'Den första volymen ger dig en komplett säsongsplan och tolv färdiga pass som du kan köra direkt på banan.',
  items: ['Säsongsplan för 40 veckor', 'Mål och teman vecka för vecka', '12 färdiga pass minut för minut', 'Pass för 60 och 90 minuter', 'Utskrivbara passkort', 'Banskisser och uppställningar', 'Coachord för varje övning', 'Lekar och matchformer', 'Nivåtester för uppföljning'],
  outro: 'Färdiga pass som gör att du kan gå in på banan med en tydlig plan.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 02',
  level: 'Alla nivåer',
  title: 'Teknik slag för slag',
  tagline: 'Lär ut varje slag med tydliga steg och rätt coachord.',
  intro: 'Den andra volymen går igenom alla grundslag steg för steg, med vanliga fel och övningar som rättar dem.',
  items: ['Forehand', 'Backhand', 'Serve', 'Retur', 'Volley', 'Smash', 'Steg för steg för varje slag', 'Coachord som spelarna förstår', 'Vanliga fel och hur du rättar dem', 'Övningar för varje slag'],
  outro: 'Ett tydligt stöd för att lära ut teknik på ett enkelt och konsekvent sätt.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 03',
  level: 'Röd → gul boll',
  title: 'Övningar & lekar per nivå',
  tagline: 'Rätt övning för rätt nivå – från minitennis till tävling.',
  intro: 'Den tredje volymen samlar över 80 övningar och lekar sorterade efter bollfärg, så att du snabbt hittar det som passar gruppen.',
  items: ['80+ övningar och lekar', 'Minitennis med röd boll', 'Orange boll', 'Grön boll', 'Gul boll', 'Övningar inför tävlingsspel', 'Matchformer och spelformer', 'Banskisser för varje övning', 'Progressioner mellan nivåerna'],
  outro: 'Färdiga övningar som håller spelarna aktiva och passet i rörelse.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 04',
  level: 'Junior & vuxen',
  title: 'Taktik & mentalt spel',
  tagline: 'Lär spelarna att tänka smart på banan.',
  intro: 'Den fjärde volymen kombinerar taktik och mental träning med praktiska övningar för både singel och dubbel.',
  items: ['5 spelsituationer', 'Taktik i singel', 'Taktik i dubbel', 'Serve- och returspel', 'Rutiner mellan bollarna', 'Hantera nerver och misstag', 'Matchförberedelse', 'Taktiska spelformer', 'Reflektionsfrågor efter match'],
  outro: 'Ett praktiskt stöd för att utveckla spelare som förstår spelet.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 05',
  level: 'Alla nivåer',
  title: 'Fys, skadeprevention & uppvärmningar',
  tagline: 'Starka, snabba och skadefria spelare.',
  intro: 'Den femte volymen innehåller färdiga uppvärmningar, fys- och fotarbetsövningar samt förebyggande träning för axel och armbåge.',
  items: ['20 färdiga uppvärmningar', 'Koordination', 'Fotarbete', 'Snabbhet och rörlighet', 'Skadeprevention för axel', 'Skadeprevention för armbåge', 'Övningar anpassade efter ålder', 'Nedvarvning efter passet', 'Utskrivbara fyskort'],
  outro: 'Strukturerade övningar som gör varje pass tryggare och mer effektivt.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 06',
  level: 'Alla nivåer',
  title: 'Utvärdering & spelaruppföljning',
  tagline: 'Visa utvecklingen – för spelarna och för föräldrarna.',
  intro: 'Den sjätte volymen ger dig verktyg för att testa, följa upp och kommunicera varje spelares utveckling.',
  items: ['Nivåtester för varje bollfärg', 'Progressionsmatriser', 'Utvecklingsmål per spelare', 'Föräldrarapporter', '100 färdiga tränarkommentarer', 'Uppföljning under säsongen', 'Mallar för utvecklingssamtal', 'Underlag för nivåindelning'],
  outro: 'Ett komplett stöd för att följa upp spelarna och visa vad träningen ger.',
  note: 'Testerna är självständigt framtagna och är inte officiella tester från något förbund.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitala volymer för tennistränare',
  text: 'Ett komplett tränarsystem som hjälper dig att planera och genomföra träningen under hela säsongen. Varje volym innehåller 40 sidor med färdiga pass, övningar och material att ta med ut på banan.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Färdiga pass minut för minut',
  text: 'Slipp börja från ett tomt papper. Tydliga passupplägg med tider, övningar och coachord hjälper dig att komma igång snabbare.'
}, {
  icon: FileTextIcon,
  title: 'Utskrivbara passkort',
  text: 'Hela passet på ett kort som kan skrivas ut och tas med ut på banan – inget mer letande i mobilen.'
}, {
  icon: ShapesIcon,
  title: 'Banskisser & övningar',
  text: 'Tydliga skisser över uppställningar, rotationer och bollbanor gör övningarna lätta att förklara och sätta igång.'
}, {
  icon: DicesIcon,
  title: 'Lekar & matchformer',
  text: 'Lekar, utmaningar och matchformer ger spelarna mer bollkontakt, mer glädje och mindre väntan.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Tester & uppföljning',
  text: 'Färdiga nivåtester och föräldrarapporter som stöd för att följa och visa spelarnas utveckling.'
}, {
  icon: CalendarCheckIcon,
  title: 'Säsongsplanering',
  text: 'Stöd för att organisera hela säsongen och välja teman och mål utifrån gruppens nivå.'
}];
export { pageSamples } from './pageSamples';
export const pageTypes = ['Färdiga pass', 'Utskrivbara passkort', 'Banskisser & övningar', 'Tester & facit'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Pass utan bana',
  text: 'Färdiga inomhus- och teoripass för dagar med regn, snö eller ingen bantid.',
  items: ['Pass med taktiktavla', 'Mental träning', 'Videoanalys', 'Teoripass inomhus'],
  benefit: 'Inga inställda pass – även när banan inte går att använda.'
}, {
  title: '52 veckors juniorstege',
  text: 'En komplett årsplan för juniorer 5–14 år, organiserad efter bollfärg.',
  items: ['Årsplan för 52 veckor', 'Juniorer 5–14 år', 'Upplägg per bollfärg', 'Tydliga steg mellan nivåerna'],
  benefit: 'En röd tråd för juniorerna hela året.'
}, {
  title: 'Stora grupper',
  text: 'Upplägg för 8–16 spelare på en bana, med stationer och rotationer som håller alla aktiva.',
  items: ['8–16 spelare på en bana', 'Stationsträning', 'Rotationer', 'Tydliga kommandon'],
  benefit: 'Mer bollkontakt och mindre köande – även i stora grupper.'
}, {
  title: 'Vuxentaktik för klubbspelare',
  text: 'Taktiska upplägg för vuxengrupper som vill vinna fler matcher.',
  items: ['Servetaktik', 'Returtaktik', 'Dubbeltaktik', 'Spelformer för vuxna'],
  benefit: 'Färdiga pass för vuxengrupperna utan extra planering.'
}, {
  title: 'Fickkort på banan',
  text: 'Visuella kort att ha med sig på banan under passen.',
  items: ['Coachord', 'Uppställningar', '3-6-10-kartor', 'Snabba påminnelser'],
  benefit: 'Allt du behöver – direkt i fickan.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Öppna TENNIS KLAR™',
  text: 'Ladda ner paketet och öppna volymen som passar din grupp.'
}, {
  title: 'Välj grupp och pass',
  text: 'Hitta rätt nivå och välj det pass, de övningar eller de lekar som passar dina spelare.'
}, {
  title: 'Förbered dig på 5 minuter',
  text: 'Gå igenom passet och coachorden och välj det material du behöver.'
}, {
  title: 'Skriv ut passkortet och gå in på banan',
  text: 'Skriv ut passkortet eller använd materialet digitalt – och gå in på banan med en tydlig plan.'
}];
export const audienceItems = ['Är klubbtränare eller tränar i en tennisskola', 'Tränar juniorgrupper med röd, orange, grön eller gul boll', 'Leder vuxengrupper och vill ha färdiga upplägg', 'Har grupper med blandade nivåer', 'Vill slippa planera varje pass från grunden', 'Vill ha fler lekar och matchformer med mer bollkontakt', 'Vill kunna visa föräldrar spelarnas utveckling', 'Vill spara kvällar och helger på planeringen'];
export const timeValueTasks = ['Leta efter övningar på YouTube', 'Planera passen', 'Rita banskisser', 'Förbereda lekar och matchformer', 'Testa och följa upp spelarna'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitala volymer – 240 sidor',
  detail: 'Årsplanering, teknik, övningar, taktik, fys och uppföljning'
}, {
  label: '5 bonusar utan extra kostnad',
  detail: 'Pass utan bana, juniorstege, stora grupper, vuxentaktik och fickkort'
}, {
  label: 'För alla nivåer',
  detail: 'Färdiga pass, passkort, banskisser och tester'
}];
export const offerIncludes = ['Omedelbar digital nedladdning', 'PDF-filer att spara', 'Utskrivbara passkort', 'Ingen fysisk produkt skickas'];
export const PRICE = '299 kr';

// Only add authentic reviews of TENNIS KLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hur får jag materialet?',
  a: 'TENNIS KLAR™ levereras digitalt. Efter köpet får du tillgång till PDF-filer som du kan ladda ner och spara. Du kan använda materialet på dator, surfplatta eller mobil och skriva ut de passkort du behöver.'
}, {
  q: 'Passar det för min klubb eller tennisskola?',
  a: 'Ja. Materialet är framtaget för tränare som leder grupper med barn, juniorer och vuxna – i klubb, tennisskola eller som privat tränare. Det är ett fristående tränarmaterial och inte en officiell produkt från något förbund.'
}, {
  q: 'Passar det för alla nivåer?',
  a: 'Ja. Materialet täcker alla nivåer – från minitennis med röd boll via orange och grön boll till gul boll och tävlingsspel. Du väljer pass och övningar utifrån gruppens nivå och ålder.'
}, {
  q: 'Kan jag verkligen förbereda ett pass på 5 minuter?',
  a: 'TENNIS KLAR™ är utformat för att göra förberedelserna snabbare. Du kan öppna en volym, välja ett färdigt pass och gå igenom det på ungefär fem minuter. Tiden kan variera beroende på passets innehåll, gruppen och behovet av anpassning.'
}, {
  q: 'Räcker materialet för en hel säsong?',
  a: 'TENNIS KLAR™ innehåller pass, övningar och en säsongsplan för 40 veckor. Du väljer, kombinerar och anpassar materialet efter din planering och spelarnas behov.'
}, {
  q: 'Finns det färdiga passkort?',
  a: 'Ja. Materialet innehåller utskrivbara passkort med övningar, tider, banskisser och coachord.'
}, {
  q: 'Ingår tester och uppföljning?',
  a: 'Ja. Paketet innehåller nivåtester, progressionsmatriser och föräldrarapporter som stöd för uppföljningen.'
}, {
  q: 'Kan jag använda materialet för spelare på olika nivåer?',
  a: 'Ja. TENNIS KLAR™ innehåller övningar och lekar för varje bollfärg som du kan välja och anpassa utifrån spelarnas nivå och behov.'
}, {
  q: 'Måste jag skriva ut allt?',
  a: 'Nej. Du väljer själv vilka sidor du skriver ut, och du kan även använda delar av materialet digitalt.'
}, {
  q: 'Ingår de fem bonusarna?',
  a: `Ja. Alla fem bonusar ingår i paketet utan extra kostnad – allt för ${PRICE}.`
}, {
  q: 'Är det en prenumeration?',
  a: 'Nej. Du betalar en gång och får tillgång till det digitala materialet. Inga återkommande avgifter.'
}, {
  q: 'Kan jag spara filerna?',
  a: 'Ja. Du kan ladda ner och spara PDF-filerna för fortsatt användning enligt produktens användarvillkor.'
}, {
  q: 'Kan jag använda materialet på mobil eller surfplatta?',
  a: 'Ja. PDF-filerna kan öppnas på dator, surfplatta och mobil. För att granska layouten på passkorten före utskrift rekommenderar vi dator eller surfplatta.'
}, {
  q: 'Finns det någon garanti?',
  a: 'Ja. TENNIS KLAR™ har 7 dagars nöjdhetsgaranti. Om materialet inte motsvarar dina förväntningar kan du kontakta oss och begära återbetalning inom garantiperioden enligt villkoren.'
}];
export const footerLinks = ['Köpvillkor', 'Integritetspolicy', 'Återbetalningspolicy', 'Kontakt'];
